import { EventEmitter, Injectable, inject } from '@angular/core';
import {
  FieldImage,
  FieldImageItem,
  FieldImageResponse,
  FieldImagesListResponse,
} from '@cropx-common/models/FieldImagesResponse';
import { ApiGatewayService } from '@cropx-web/services/api-gateway/ApiGatewayService';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { iif, insertItem, patch, updateItem } from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';

import { ApiService } from '../services/network/ApiService';

// NOTE: This state contains the old images from app-rep, new satelitte state should be in satellite.state.ts

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace SatelliteImages {
  const type = 'SatelliteImages';

  export class Fetch {
    static readonly type = `[${type}] Fetch`;
    constructor(public fieldId: string) {}
  }

  export class FetchImage {
    static readonly type = `[${type}] FetchImage`;
    constructor(
      public fieldId: string,
      public id: number,
    ) {}
  }

  export class SetAvailableImages {
    static readonly type = `[${type}] SetAvailableImages`;
    constructor(
      public fieldId: string,
      public availableImages: FieldImageItem[],
      public loading = false,
    ) {}
  }

  export class UpdateImages {
    static readonly type = `[${type}] UpdateImage`;
    constructor(
      public fieldId: string,
      public image: FieldImage,
      public loading = false,
    ) {}
  }
}

export type AvailableImageSet = Record<string, AvailableImages>;

export interface AvailableImages {
  items: FieldImageItem[];
  loading: boolean;
}
export type ImageSet = Record<string, ImageItem[]>;

export interface ImageItem extends FieldImage {
  loading: boolean;
}
export interface SatelliteImagesStateModel {
  available: AvailableImageSet;
  images: ImageSet;
}

@State<SatelliteImagesStateModel>({
  name: 'satelliteImages',
  defaults: {
    available: {},
    images: {},
  },
})
@Injectable()
export class SatelliteImagesState {
  private oldApi = inject(ApiService);
  private api = inject(ApiGatewayService);

  @Action(SatelliteImages.Fetch)
  fetch(
    ctx: StateContext<SatelliteImagesStateModel>,
    action: SatelliteImages.Fetch,
  ): Observable<FieldImageItem[]> {
    return new Observable<FieldImageItem[]>((s) => {
      ctx.dispatch(
        new SatelliteImages.SetAvailableImages(action.fieldId, [], true),
      );
      const respEvent = new EventEmitter<FieldImagesListResponse>();
      const err = new EventEmitter<any>();

      this.oldApi.fieldApi.getFieldImagesList(action.fieldId, respEvent, err);

      respEvent.pipe(first()).subscribe((resp) => {
        ctx.dispatch(
          new SatelliteImages.SetAvailableImages(action.fieldId, resp.content),
        );
        s.next(resp.content);
        s.complete();
      });
    });
  }

  @Action(SatelliteImages.SetAvailableImages)
  setAvailableImages(
    ctx: StateContext<SatelliteImagesStateModel>,
    action: SatelliteImages.SetAvailableImages,
  ): void {
    ctx.setState(
      patch<SatelliteImagesStateModel>({
        available: patch<AvailableImageSet>({
          [action.fieldId]: {
            items: action.availableImages,
            loading: action.loading,
          },
        }),
      }),
    );
  }

  @Action(SatelliteImages.FetchImage)
  fetchImage(
    ctx: StateContext<SatelliteImagesStateModel>,
    action: SatelliteImages.FetchImage,
  ): Observable<FieldImage> {
    return new Observable<FieldImage>((s) => {
      const existing = ctx
        .getState()
        .images[action.fieldId]?.find((i) => i.id === action.id);

      if (existing) {
        s.next(existing);
        s.complete();
        return;
      }

      const respEvent = new EventEmitter<FieldImageResponse>();
      const err = new EventEmitter<any>();

      ctx.dispatch(
        new SatelliteImages.UpdateImages(
          action.fieldId,
          { id: action.id, image: '', imageDate: 0 },
          true,
        ),
      );
      this.oldApi.fieldApi.getFieldSatelliteLayerById(
        action.fieldId,
        action.id,
        respEvent,
        err,
      );

      respEvent.pipe(first()).subscribe((resp) => {
        ctx.dispatch(
          new SatelliteImages.UpdateImages(action.fieldId, resp.content),
        );
        s.next(resp.content);
        s.complete();
      });
    });
  }

  @Action(SatelliteImages.UpdateImages)
  updateImages(
    ctx: StateContext<SatelliteImagesStateModel>,
    action: SatelliteImages.UpdateImages,
  ): void {
    ctx.setState(
      patch<SatelliteImagesStateModel>({
        images: iif(
          (sets) => !!sets[action.fieldId],
          patch({
            [action.fieldId]: iif(
              (images) => images.some((img) => img.id === action.image.id),
              updateItem((data) => data.id === action.image.id, {
                ...action.image,
                loading: action.loading,
              }),
              insertItem({ ...action.image, loading: action.loading }),
            ),
          }),
          {
            [action.fieldId]: [{ ...action.image, loading: action.loading }],
          },
        ),
      }),
    );
  }
}

export class SatelliteImagesSelectors {
  @Selector([SatelliteImagesState])
  static availableImages(state: SatelliteImagesStateModel): AvailableImageSet {
    return state.available;
  }

  @Selector([SatelliteImagesState])
  static images(state: SatelliteImagesStateModel): ImageSet {
    return state.images;
  }
}
